import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Save } from 'lucide-react';
import BudgetPlanner from '../../components/BudgetEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// A member's personal budget link (/budget/{token}) — no login. The AD / Treasurer
// creates it under Finance ▸ Budget ▸ Member links (docs/AD_DASHBOARD_AND_FINANCE.md §2b).
// The member works the same planner — line items, calculators, notes — but not the name,
// season, settings, resolution, approval or sharing. A submitted / approved budget is read-only.
export default function PublicBudgetPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [b, setB] = useState(null);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const url = `${API_URL}/api/ad-finance/public/budget/${token}`;

  useEffect(() => {
    axios.get(url).then((r) => { setD(r.data); setB(r.data.budget); })
      .catch((e) => setErr(e?.response?.data?.detail || 'This budget link is not valid.'));
  }, [url]);
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(url, { sections: b.sections, calculators: b.calculators, notes: b.notes });
      setB(data.budget); setDirty(false); toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
        <div className="max-w-md text-center text-slate-700 dark:text-slate-200"><AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-2" />{err}</div>
      </div>
    );
  }
  if (!b) return <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const locked = ['submitted', 'approved'].includes(b.status);
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4" data-testid="public-budget">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <div>
            <div className="text-xs text-slate-500">{d.org}</div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">{b.name} <span className="text-sm font-normal text-slate-500">{b.season} · version {b.version}</span></h1>
            <div className="text-[11px] text-slate-500">Editing as {d.editor} · last saved {String(b.updated_at || '').slice(0, 16).replace('T', ' ')} by {b.updated_by}</div>
          </div>
          {!locked && (
            <button type="button" onClick={save} disabled={busy || !dirty} data-testid="public-budget-save"
              className="ml-auto inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          )}
        </div>
        <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-3">
          <BudgetPlanner budget={b} member orgName={d.org} onChange={(patch) => { setB((x) => ({ ...x, ...patch })); setDirty(true); }} />
        </div>
      </div>
    </div>
  );
}
